import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { Card, Select } from "@libs/rubick";
import { AuditLogsFilters } from "@/components/audit-logs";
import { ProductsDropdown } from "@/components/products/ProductsDropdown";
import { Button } from "@/components/ui";
import { MetricCard } from "@/components/ui/MetricCard";
import { FILTER_VALUES } from "@/constants/filters";
import { getTimeRangeDateRange } from "@/constants/timeRange";
import useAuditLogsStore from "@/context/audit-logs/useAuditLogsStore";
import { useAuditLogs } from "@/hooks/audit-logs";
import { useApiKeys } from "@/hooks/api-keys";
import { useAuditLogsPagination } from "@/hooks/useAuditLogsPagination";
import { DashboardPageFilters, DashboardPageLayout, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { filterAuditLogsByProduct, formatGeography, getAuditLogStatusColor, getStatusText, parseAuditLogTimestamp } from "@/utils/auditLogs";
import { formatBytesPrecise } from "@/utils/formatters";
import { getHttpResponseMessage } from "@/utils/httpError";
function toDateRange(timeRange) {
    const { startDate, endDate } = getTimeRangeDateRange(timeRange);
    return {
        startDate,
        endDate
    };
}
const getAuditLogRowId = (log, index)=>{
    if (log.requestId) {
        return log.requestId;
    }
    return `${log.apiKeyId || "unknown"}-${log.requestTime || "unknown"}-${index}`;
};
const formatTableValue = (value)=>{
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    return String(value);
};
const formatRequestSize = (requestSize)=>{
    if (typeof requestSize !== "number") {
        return "-";
    }
    return formatBytesPrecise(requestSize, 0);
};
const formatRequestTimestamp = (requestTime)=>{
    if (!requestTime) {
        return "-";
    }
    const parsedDate = parseAuditLogTimestamp(requestTime);
    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }
    return parsedDate.toLocaleString();
};
const formatCardCount = (value)=>{
    if (value === null) {
        return "-";
    }
    return value.toLocaleString();
};
export default function AuditLogsPage() {
    const [selectedProduct, setSelectedProduct] = useState(FILTER_VALUES.ALL);
    const [expandedRowIds, setExpandedRowIds] = useState(new Set());
    const { filters, setTimeRange, setSelectedApiKey } = useAuditLogsStore();
    const dateRange = useMemo(()=>toDateRange(filters.timeRange), [
        filters.timeRange
    ]);
    const { pageSize, currentToken, currentPage, hasPreviousPage, resetPagination, goToNextPage, goToPreviousPage, setPageSize } = useAuditLogsPagination({
        initialPageSize: 50
    });
    const activeProductCode = selectedProduct === FILTER_VALUES.ALL ? "" : selectedProduct;
    const { data: apiKeysData } = useApiKeys(activeProductCode);
    const apiKeyNameMap = useMemo(()=>{
        const map = new Map();
        apiKeysData?.items?.forEach((apiKey)=>{
            if (apiKey.name) {
                map.set(apiKey.id, apiKey.name);
            }
        });
        return map;
    }, [
        apiKeysData
    ]);
    // Reset API key filter when product changes (selected key might not exist in new product)
    useEffect(()=>{
        if (filters.selectedApiKey !== FILTER_VALUES.ALL) {
            // Check if the currently selected API key exists in the new product's keys
            const apiKeyExists = apiKeysData?.items?.some((key)=>key.id === filters.selectedApiKey);
            if (!apiKeyExists) {
                setSelectedApiKey(FILTER_VALUES.ALL);
            }
        }
    }, [
        selectedProduct,
        apiKeysData?.items,
        filters.selectedApiKey,
        setSelectedApiKey
    ]);
    const { data: auditLogsData, isLoading, error } = useAuditLogs(dateRange.startDate, dateRange.endDate, filters.selectedApiKey === FILTER_VALUES.ALL ? undefined : filters.selectedApiKey, pageSize, currentToken);
    useEffect(()=>{
        resetPagination();
    }, [
        filters.timeRange,
        filters.selectedApiKey,
        resetPagination,
        selectedProduct
    ]);
    const logs = useMemo(()=>{
        if (!auditLogsData) {
            return [];
        }
        const sortedLogs = [
            ...auditLogsData.logs || []
        ].sort((a, b)=>{
            return parseAuditLogTimestamp(b.requestTime).getTime() - parseAuditLogTimestamp(a.requestTime).getTime();
        });
        return filterAuditLogsByProduct({
            logs: sortedLogs,
            selectedApiKey: filters.selectedApiKey,
            selectedProduct,
            apiKeys: apiKeysData?.items
        });
    }, [
        auditLogsData,
        filters.selectedApiKey,
        selectedProduct,
        apiKeysData?.items
    ]);
    const tableLogs = useMemo(()=>{
        return logs.map((log)=>({
                ...log,
                apiKeyLabel: apiKeyNameMap.get(log.apiKeyId) || log.apiKeyId || "-"
            }));
    }, [
        logs,
        apiKeyNameMap
    ]);
    const summaryStats = useMemo(()=>{
        const hasAuditData = Boolean(auditLogsData);
        const pageLogCount = tableLogs.length;
        const totalRequests = hasAuditData ? pageLogCount : null;
        const rateLimitedCount = hasAuditData ? tableLogs.filter((log)=>log.status === 429).length : null;
        const errorCount = hasAuditData ? tableLogs.filter((log)=>typeof log.status === "number" && log.status >= 400).length : null;
        const activeApiKeyIds = new Set((apiKeysData?.items || []).filter((apiKey)=>apiKey.status?.toLowerCase() === "active").map((apiKey)=>apiKey.id));
        const activeKeysCount = apiKeysData?.items ? activeApiKeyIds.size : null;
        const activeKeysWithActivity = apiKeysData?.items ? new Set(tableLogs.map((log)=>log.apiKeyId).filter((apiKeyId)=>activeApiKeyIds.has(apiKeyId))).size : null;
        const rateLimitedPct = rateLimitedCount !== null && pageLogCount > 0 ? (rateLimitedCount / pageLogCount * 100).toFixed(1) : null;
        const errorRatePct = errorCount !== null && pageLogCount > 0 ? (errorCount / pageLogCount * 100).toFixed(1) : null;
        return {
            pageLogCount,
            totalRequests,
            rateLimitedCount,
            errorCount,
            activeKeysCount,
            activeKeysWithActivity,
            rateLimitedPct,
            errorRatePct
        };
    }, [
        auditLogsData,
        tableLogs,
        apiKeysData?.items
    ]);
    const hasNextPage = !!auditLogsData?.pagination?.nextToken;
    const toggleRowExpansion = (rowId)=>{
        setExpandedRowIds((prev)=>{
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    };
    const handleNextPage = ()=>{
        goToNextPage(auditLogsData?.pagination?.nextToken);
    };
    const handlePreviousPage = ()=>{
        goToPreviousPage();
    };
    const handlePageSizeChange = (newSize)=>{
        setPageSize(newSize);
    };
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "Audit Logs",
        description: "Monitor API request activity and track usage patterns",
        children: [
            /*#__PURE__*/ _jsx(DashboardPageFilters, {
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-full max-w-xs",
                    children: /*#__PURE__*/ _jsx(ProductsDropdown, {
                        value: selectedProduct,
                        onChange: setSelectedProduct
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4",
                children: [
                    /*#__PURE__*/ _jsx(MetricCard, {
                        helperText: summaryStats.totalRequests === null ? "-" : `${summaryStats.pageLogCount} on current page`,
                        label: "Total Requests",
                        value: formatCardCount(summaryStats.totalRequests)
                    }),
                    /*#__PURE__*/ _jsx(MetricCard, {
                        helperText: summaryStats.rateLimitedPct ? `${summaryStats.rateLimitedPct}% of current page` : "-",
                        label: "Rate Limited (429)",
                        value: formatCardCount(summaryStats.rateLimitedCount)
                    }),
                    /*#__PURE__*/ _jsx(MetricCard, {
                        helperText: summaryStats.errorRatePct ? `${summaryStats.errorRatePct}% error rate` : "-",
                        label: "Errors (4xx/5xx)",
                        value: formatCardCount(summaryStats.errorCount)
                    }),
                    /*#__PURE__*/ _jsx(MetricCard, {
                        helperText: summaryStats.activeKeysWithActivity === null ? "-" : `${summaryStats.activeKeysWithActivity} with activity on current page`,
                        label: "Active Keys",
                        value: formatCardCount(summaryStats.activeKeysCount)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DashboardPageSection, {
                children: /*#__PURE__*/ _jsx(AuditLogsFilters, {
                    filters: filters,
                    onTimeRangeChange: setTimeRange,
                    onApiKeyChange: setSelectedApiKey,
                    productCode: activeProductCode
                })
            }),
            /*#__PURE__*/ _jsx(Card, {
                variant: "bordered",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm text-gray-600",
                            children: [
                                "Showing",
                                " ",
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-gray-900",
                                    children: tableLogs.length
                                }),
                                " ",
                                "audit log",
                                tableLogs.length !== 1 ? "s" : "",
                                " on page",
                                " ",
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-gray-900",
                                    children: currentPage
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    htmlFor: "pageSize",
                                    className: "text-sm text-gray-600 whitespace-nowrap",
                                    children: "Items per page:"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    id: "pageSize",
                                    value: pageSize,
                                    onChange: (e)=>handlePageSizeChange(Number(e.target.value)),
                                    className: "w-24",
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: 25,
                                            children: "25"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: 50,
                                            children: "50"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: 100,
                                            children: "100"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: 200,
                                            children: "200"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "space-y-3",
                children: isLoading ? /*#__PURE__*/ _jsxs(Card, {
                    variant: "bordered",
                    className: "text-center",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex justify-center items-center",
                            children: /*#__PURE__*/ _jsx(CircularProgress, {})
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-gray-500 mt-4",
                            children: "Loading audit logs..."
                        })
                    ]
                }) : error ? /*#__PURE__*/ _jsx(Card, {
                    variant: "bordered",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "bg-red-50 border border-red-200 rounded-lg p-4",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-red-800",
                            children: getHttpResponseMessage(error) || "Failed to load audit logs"
                        })
                    })
                }) : tableLogs.length === 0 ? /*#__PURE__*/ _jsx(Card, {
                    variant: "bordered",
                    className: "text-center",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-500",
                        children: "No audit logs found for the selected filters."
                    })
                }) : /*#__PURE__*/ _jsx(Card, {
                    variant: "bordered",
                    className: "overflow-hidden",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "overflow-x-auto",
                        children: /*#__PURE__*/ _jsxs("table", {
                            className: "min-w-full divide-y divide-gray-200",
                            children: [
                                /*#__PURE__*/ _jsx("thead", {
                                    className: "bg-gray-50",
                                    children: /*#__PURE__*/ _jsxs("tr", {
                                        children: [
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "Status"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "Geography"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "TLS Version"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "API Key"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "Request Size"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "Timestamp"
                                            }),
                                            /*#__PURE__*/ _jsx("th", {
                                                className: "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600",
                                                children: "Request ID"
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx("tbody", {
                                    className: "divide-y divide-gray-200 bg-white",
                                    children: tableLogs.map((log, index)=>{
                                        const rowId = getAuditLogRowId(log, index);
                                        const isExpanded = expandedRowIds.has(rowId);
                                        return /*#__PURE__*/ _jsxs(Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsxs("tr", {
                                                    className: "cursor-pointer hover:bg-gray-50",
                                                    onClick: ()=>toggleRowExpansion(rowId),
                                                    onKeyDown: (event)=>{
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            toggleRowExpansion(rowId);
                                                        }
                                                    },
                                                    role: "button",
                                                    tabIndex: 0,
                                                    "aria-expanded": isExpanded,
                                                    "aria-label": isExpanded ? "Collapse request details" : "Expand request details",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm text-gray-900",
                                                            children: typeof log.status === "number" ? /*#__PURE__*/ _jsxs("span", {
                                                                className: `rounded px-2 py-1 text-xs font-semibold ${getAuditLogStatusColor(log.status)}`,
                                                                children: [
                                                                    log.status,
                                                                    " ",
                                                                    getStatusText(log.status)
                                                                ]
                                                            }) : "-"
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm text-gray-600",
                                                            children: formatGeography(log.city, log.countryCode ?? log.country)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm text-gray-600",
                                                            children: formatTableValue(log.tlsVersion)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900",
                                                            children: formatTableValue(log.apiKeyLabel)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm text-gray-600",
                                                            children: formatRequestSize(log.requestSize)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "whitespace-nowrap px-4 py-3 text-sm text-gray-600",
                                                            children: formatRequestTimestamp(log.requestTime)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "max-w-[260px] truncate px-4 py-3 text-xs font-mono text-gray-500",
                                                            title: formatTableValue(log.requestId),
                                                            children: formatTableValue(log.requestId)
                                                        })
                                                    ]
                                                }),
                                                isExpanded && /*#__PURE__*/ _jsx("tr", {
                                                    className: "bg-gray-50",
                                                    children: /*#__PURE__*/ _jsx("td", {
                                                        colSpan: 7,
                                                        className: "px-4 py-4",
                                                        children: /*#__PURE__*/ _jsx("pre", {
                                                            className: "overflow-x-auto rounded-lg bg-gray-100 p-4 font-mono text-sm text-gray-800",
                                                            children: /*#__PURE__*/ _jsx("code", {
                                                                children: JSON.stringify(log, null, 2)
                                                            })
                                                        })
                                                    })
                                                })
                                            ]
                                        }, rowId);
                                    })
                                })
                            ]
                        })
                    })
                })
            }),
            !isLoading && !error && tableLogs.length > 0 && /*#__PURE__*/ _jsx(Card, {
                variant: "bordered",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            onClick: handlePreviousPage,
                            disabled: !hasPreviousPage,
                            variant: "simple",
                            color: "navy-light",
                            children: "Previous"
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-sm text-gray-600",
                            children: [
                                "Page ",
                                currentPage
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            onClick: handleNextPage,
                            disabled: !hasNextPage,
                            variant: "simple",
                            color: "navy-light",
                            children: "Next"
                        })
                    ]
                })
            })
        ]
    });
}
